/**
 * Utility functions for detecting and handling text direction (RTL/LTR)
 */

// RTL languages pattern - covers major RTL scripts
const RTL_LANGUAGES = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

/**
 * Detects if text contains RTL characters
 * @param text - The text to analyze
 * @returns true if text contains RTL characters, false otherwise
 */
export const isRTLText = (text: string): boolean => {
  if (!text || typeof text !== 'string') return false;
  return RTL_LANGUAGES.test(text);
};

/**
 * Gets the appropriate text direction for a given text
 * @param text - The text to analyze
 * @returns 'rtl' if text contains RTL characters, 'ltr' otherwise
 */
export const getTextDirection = (text: string): 'rtl' | 'ltr' => {
  return isRTLText(text) ? 'rtl' : 'ltr';
};

/**
 * Gets text alignment based on text direction
 * @param text - The text to analyze
 * @returns 'right' for RTL text, 'left' for LTR text
 */
export const getTextAlignment = (text: string): 'left' | 'right' => {
  return isRTLText(text) ? 'right' : 'left';
};

/**
 * Gets flex direction for message layout based on text direction and message type
 * @param text - The text to analyze
 * @param isUserMessage - Whether this is a user message
 * @returns appropriate flex direction
 */
export const getMessageFlexDirection = (text: string, isUserMessage: boolean): 'row' | 'row-reverse' => {
  const isRTL = isRTLText(text);
  
  if (isUserMessage) {
    // User messages: RTL text should flow from right, LTR from right (default)
    return isRTL ? 'row' : 'row-reverse';
  } else {
    // Assistant messages: RTL text should flow from right, LTR from left (default)
    return isRTL ? 'row-reverse' : 'row';
  }
};

/**
 * Gets CSS styles for text direction support
 * @param text - The text to analyze
 * @returns CSS properties object for text direction
 */
export const getTextDirectionStyles = (text: string) => {
  const direction = getTextDirection(text);
  const textAlign = getTextAlignment(text);
  
  return {
    direction,
    textAlign,
    unicodeBidi: 'embed' as const,
  };
};

/**
 * Gets appropriate placeholder text based on detected input language
 * @param currentText - The current text in the input
 * @param defaultPlaceholder - Default placeholder text to use
 * @param rtlPlaceholder - RTL placeholder text to use (optional)
 * @returns Appropriate placeholder text
 */
export const getLocalizedPlaceholder = (
  currentText: string,
  defaultPlaceholder?: string,
  rtlPlaceholder?: string
): string => {
  if (isRTLText(currentText)) {
    // If the user is typing in RTL, show an RTL placeholder
    // Use provided RTL placeholder or fallback to Arabic
    return rtlPlaceholder || "اكتب رسالتك هنا..."; // "Type your message here..." in Arabic
  }
  
  // Default English placeholder
  return defaultPlaceholder || "Type your message here...";
};
